<template>
  <div 
    class="fts-editor" 
    :class="[
      {'editor--float': floatMenuBar && isFloatMenuBar},
      {'editor--error': error}
    ]"
  >
    <!-- bar -->
    <editor-menu-bar
      v-slot="{ commands, isActive }"
      :editor="editor"
      ref="editorMenuBar"
    >
      <div class="menubar" v-if="!floatMenuBar || (floatMenuBar && isFloatMenuBar)">
        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.bold() }"
          tabindex="-1"
          @click="commands.bold"
        >
          <font-awesome-icon :icon="['fas', 'bold']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.italic() }"
          tabindex="-1"
          @click="commands.italic"
        >
          <font-awesome-icon :icon="['fas', 'italic']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.strike() }"
          tabindex="-1"
          @click="commands.strike"
        >
          <font-awesome-icon :icon="['fas', 'strikethrough']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.underline() }"
          tabindex="-1"
          @click="commands.underline"
        >
          <font-awesome-icon :icon="['fas', 'underline']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.paragraph() }"
          tabindex="-1"
          @click="commands.paragraph"
        >
          <font-awesome-icon :icon="['fas', 'paragraph']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          tabindex="-1"
          @click="commands.heading({ level: 1 })"
        >H1</button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          tabindex="-1"
          @click="commands.heading({ level: 2 })"
        >H2</button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 3 }) }"
          tabindex="-1"
          @click="commands.heading({ level: 3 })"
        >H3</button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.bullet_list() }"
          tabindex="-1"
          @click="commands.bullet_list"
        >
          <font-awesome-icon :icon="['fas', 'list-ul']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.ordered_list() }"
          tabindex="-1"
          @click="commands.ordered_list"
        >
          <font-awesome-icon :icon="['fas', 'list-ol']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.superscript() }"
          tabindex="-1"
          @click="commands.superscript"
        >
          <font-awesome-icon :icon="['fas', 'superscript']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          :class="{ 'is-active': isActive.subscript() }"
          tabindex="-1"
          @click="commands.subscript"
        >
          <font-awesome-icon :icon="['fas', 'subscript']" fixed-width />
        </button>

        <button
          type="button"
          class="menubar__button"
          tabindex="-1"
          @click="show = true"
          v-if="isBeta"
        >
          <font-awesome-icon :icon="['fas', 'code']" fixed-width />
          <fts-release-badge class="fts-align-top" />
        </button>
        
        <div class="menubar-dummy" />
      </div>
    </editor-menu-bar>
    <!-- content -->
    <editor-content
      class="editor__content fts-editor__content"
      :editor="editor"
      :key="uid"
      ref="editorContent"
      v-if="editor"
    />
    <!-- code viewer -->
    <fts-modal
      :append-to-body="appendToBody"
      title="HTML Editor" 
      :class="referenceFor"
      @close="show=false"
      v-if="show"
    >
      <template #body>
        <fts-script-editor
          editor-id="ftsContentScriptEditor"
          v-model="formData[item.fieldName]"
          lang="html"
          label="Content"
          disabled-dictionary
        >
          <template #label="{ label }">
            {{label}} <fts-release-badge class="fts-align-top" />
          </template>
        </fts-script-editor>
      </template>
    </fts-modal>
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import {
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Italic,
  Link,
  Strike,
  Underline,
  History,
  Placeholder,
} from "tiptap-extensions";

import { Superscript } from 'tiptap-extension-superscript';
import { Subscript } from 'tiptap-extension-subscript';

import { authorizationStore } from '../store/authorizationStore';
import { clientStore } from '../store/clientStore';
import { htmlValidator } from '../commons/validator';

import FTSModal from "./FTSModal";
import FTSScriptEditor from "../design/FTSScriptEditor";

export default {
  components: {
    EditorContent,
    EditorMenuBar,
    "fts-modal": FTSModal,
    "fts-script-editor": FTSScriptEditor
  },
  props: {
    appendToBody: Boolean,
    referenceFor: String,
    floatMenuBar: Boolean,
    error: Boolean,
    item: Object,
    formData: Object,
    data: String
  },
  data: () => ({
    uid: null,
    editor: null,
    editorChange: false,
    show: false,
    isFloatMenuBar: false,
  }),
  methods: {
    clickHandler(event, relatedTarget) {
      const target = relatedTarget || event.target;
      if (this.$el === target || this.$el.contains(target)) {
        this.isFloatMenuBar = true;
      } else {
        this.isFloatMenuBar = false;
      }
    },
    updateContentHeight() {
      this.$nextTick(() => {
        try {
          const elOffsetHeight = this.$refs.editorContent.$el.offsetHeight;
          elOffsetHeight && this.$emit('updateContentHeight', `${elOffsetHeight}px`);
        } catch(err) {
          console.warn('[DOMException]', err);
        }
      });
    },
  },
  computed: {
    isBeta() {
      return authorizationStore.getters.isBeta || clientStore.getters.workspaceBeta;
    },
  },
  watch: {
    data() {
      if (this.editor && !this.editorChange) {
        let val = this.formData[this.item.fieldName] || '';
        this.editor.setContent(val, false);
      }
      this.editorChange = false;
    }
  },
  mounted() {
    this.uid = this._uid
    this.editor = new Editor({
      extensions: [
        // new Blockquote(),
        new BulletList(),
        // new CodeBlock(),
        new HardBreak(),
        new Heading({ levels: [1, 2, 3] }),
        new HorizontalRule(),
        new ListItem(),
        new OrderedList(),
        new TodoItem(),
        new TodoList(),
        new Link(),
        new Bold(),
        // new Code(),
        new Italic(),
        new Strike(),
        new Underline(),
        new History(),
        new Superscript(),
        new Subscript(),
        new Placeholder({
          emptyEditorClass: 'is-editor-empty',
          emptyNodeClass: 'is-empty',
          showOnlyWhenEditable: true,
          showOnlyCurrent: true,
          emptyNodeText: (node) => {
            return "write something...";
          },
        }),
      ],
      content: this.data,
      parseOptions: {
        preserveWhitespace: 'full',
      },
      onUpdate: ({ getHTML }) => {
        /* to-do break-line issue */
        const state = getHTML();
        const value = htmlValidator(state) ? state : null;
        this.$set(this.formData, this.item.fieldName, value);
        this.$emit('input', value);
        this.editorChange = true;
        this.updateContentHeight();
      },
      onFocus: () => {
        this.floatMenuBar && (this.isFloatMenuBar = true);
      },
      onBlur: ({ event }) => {
        this.floatMenuBar && this.clickHandler(event, event.relatedTarget);
        this.$emit('blur', event);
      },
    });
    this.$nextTick(() => {
      this.updateContentHeight();
    });
    this.floatMenuBar && window.addEventListener('click', this.clickHandler);
  },
  beforeDestroy() {
    this.editor && this.editor.destroy();
    this.floatMenuBar && window.removeEventListener('click', this.clickHandler);
  }
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-editor {
  background-color: var(--fts-form-control-background);
  color: var(--fts-form-control);
  .menubar {
    display: flex;
    flex-wrap: wrap;
    border: 1px solid var(--fts-form-control-border);
    border-bottom: 0;
    background-color: var(--fts-form-control-background);
    button {
      @include user-select-none;
      padding: 0.375rem;
      background-color: inherit;
      color: var(--fts-form-label);
      border: 0;
      font-weight: 700;
      font-size: 0.75rem;
      line-height: 1rem;
      text-align: center;
      vertical-align: middle;
      white-space: nowrap;
      &:hover {
        background-color: var(--fts-gray-100);
        cursor: pointer;
      }
      &.is-active {
        color: var(--fts-primary);
      }
    }
    .menubar-dummy {
      flex: 1;
      background-color: inherit;
    }
  }
  .editor__content {    
    min-height: $form-control-height;
    border: 1px solid var(--fts-form-control-border);
    font-weight: initial;
    text-decoration: initial;
    color: var(--fts-text);
    &:focus-within {
      border-color: var(--fts-primary);
      box-shadow: $input-box-shadow;
      -webkit-box-shadow: $input-box-shadow;
    }
    .ProseMirror {
      flex: 1;
      padding: 0.375rem 0.75rem;
      font-size: var(--inistate-label-text-14);
      line-height: var(--inistate-paragraph-text-14--line-height);
      & *.is-empty:nth-child(1)::before {
        content: attr(data-empty-text);
        float: left;
        color: var(--fts-form-control-placeholder);
        pointer-events: none;
        height: 0;
      }
    }
  }
  &.editor--float {
    position: relative;
    .menubar {
      position: absolute;
      bottom: calc(100% + 0.375rem);
      flex-wrap: nowrap;
      box-shadow: var(--fts-elevation-3);
      border: 0;
      background-color: var(--fts-popover-alt-background);
      z-index: 2;
    }
  }
  &.editor--error {
    .editor__content {
      border-color: $input-error-border;
      &:focus-within {
			  box-shadow: $input-error-box-shadow;
			  -webkit-box-shadow: $input-error-box-shadow;
		  }
    }
  }
}
</style>